import {
    clearValidationMessage,
    createRemoveFormData,
    createUploadFormData,
    createUploadState,
    dispatchUploadEvent,
    formatBytes,
    getPrefilledFiles,
    getServerId,
    getUploadElements,
    hasPendingUploads,
    hasUploadedReferences,
    setHiddenFileValue,
    setValidationMessage,
    validateSelectedFile,
    xhrPost,
} from './Upload.utils.js';

export function initUpload(root) {
    if (!root || root.UploadInit) return;

    root.UploadInit = true;
    root.dataset.sdsUpload = 'true';

    const elements = getUploadElements(root);
    if (!elements) return;

    const state = createUploadState(root, elements);

    prefillFiles(state);
    syncUploadVisibility(state);
    bindUploadEvents(state);
}

function bindUploadEvents(state) {
    state.input.addEventListener('change', () => {
        addFiles(state, state.input.files);
        state.input.value = '';
    });

    state.root.addEventListener('sds-upload:validate', (event) => {
        if (!validate(state, { touched: true })) {
            event.preventDefault();
        }
    });

    state.drop.addEventListener('dragenter', (event) => {
        event.preventDefault();
        state.drop.dataset.dragging = 'true';
    });

    state.drop.addEventListener('dragover', (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
    });

    state.drop.addEventListener('dragleave', (event) => {
        if (!state.drop.contains(event.relatedTarget)) {
            state.drop.dataset.dragging = 'false';
        }
    });

    state.drop.addEventListener('drop', (event) => {
        event.preventDefault();
        state.drop.dataset.dragging = 'false';

        if (!state.drop.hidden) {
            addFiles(state, event.dataTransfer.files);
        }
    });
}

function addFiles(state, fileList) {
    clearError(state);

    let hasValidationError = false;
    let startedUploads = 0;

    for (const file of fileList) {
        const error = validateSelectedFile(state, file);

        if (error) {
            hasValidationError = true;
            setError(state, error);
            continue;
        }

        startedUploads += 1;
        uploadFile(state, file);
    }

    syncUploadVisibility(state);

    if (!hasValidationError && startedUploads === 0) {
        validateWhenUploadsSettled(state);
    }
}

function prefillFiles(state) {
    for (const file of getPrefilledFiles(state)) {
        if (state.files.size >= state.maxFiles) break;

        addPrefilledFile(state, file);
    }

    if (hasUploadedReferences(state)) {
        clearError(state);
    }
}

function addPrefilledFile(state, file) {
    const id = String(file.id);

    if (state.files.has(id)) return;

    const item = createFileItem(state, id, file);

    setHiddenFileValue(item.hidden, state.fieldName, id);

    if (item.progress) {
        item.progress.remove();
    }

    item.element.dataset.prefilled = 'true';

    state.files.set(id, {
        file: {
            name: file.name || id,
            size: Number(file.size) || 0,
        },
        item,
        serverId: id,
        prefilled: true,
    });

    state.list.append(item.element);
}

async function uploadFile(state, file) {
    const id = crypto.randomUUID();
    const item = createFileItem(state, id, file);

    state.files.set(id, { file, item, serverId: null, prefilled: false });
    state.list.append(item.element);
    syncUploadVisibility(state);

    try {
        const response = await xhrPost(
            state.uploadUrl,
            createUploadFormData(state, file),
            (progress) => {
                if (item.progress) {
                    item.progress.value = progress;
                }
            }
        );

        if (response?.error || response?.Error) {
            throw new Error(response.error || response.Error);
        }

        const serverId = getServerId(response);

        if (!serverId) {
            throw new Error('Upload response must contain id, Id, fileId, FileId, uploadId, UploadId, name, or Name.');
        }

        const entry = state.files.get(id);
        if (!entry) return;

        entry.serverId = serverId;
        setHiddenFileValue(item.hidden, state.fieldName, serverId);

        if (item.progress) {
            item.progress.value = 100;
        }

        syncUploadVisibility(state);
        clearError(state);
        validateWhenUploadsSettled(state);

        dispatchUploadEvent(state, 'sds-upload:file-added', { file, serverId });
    } catch (error) {
        state.files.delete(id);
        item.element.remove();

        syncUploadVisibility(state);
        setError(state, state.root.dataset.errorServer || error.message || 'Upload failed.');

        dispatchUploadEvent(state, 'sds-upload:error', { file, error });

        console.error('Upload error:', error);
    }
}

function createFileItem(state, id, file) {
    const fragment = state.template.content.cloneNode(true);
    const element = fragment.querySelector('.file');
    const name = fragment.querySelector('[data-upload-file-name]');
    const meta = fragment.querySelector('[data-upload-file-meta]');
    const progress = fragment.querySelector('[data-upload-progress]');
    const remove = fragment.querySelector('[data-upload-remove]');
    const hidden = fragment.querySelector('[data-upload-hidden]');

    name.textContent = file.name || '';
    meta.textContent = formatBytes(file.size);

    remove.addEventListener('click', async () => {
        await removeFile(state, id);
    });

    return { element, progress, hidden };
}

async function removeFile(state, id) {
    const entry = state.files.get(id);
    if (!entry) return;

    if (entry.serverId && state.removeUrl) {
        const removed = await removeServerFile(state, entry.serverId);
        if (!removed) return;
    }

    entry.item.element.remove();
    state.files.delete(id);

    syncUploadVisibility(state);

    dispatchUploadEvent(state, 'sds-upload:file-removed', {
        serverId: entry.serverId,
    });

    validate(state);
}

async function removeServerFile(state, serverId) {
    try {
        await xhrPost(state.removeUrl, createRemoveFormData(state, serverId));
        return true;
    } catch (error) {
        setError(state, state.root.dataset.errorServer || error.message || 'Remove failed.');
        return false;
    }
}

function validate(state, { touched = false } = {}) {
    if (touched && state.field) {
        state.field.dataset.touched = 'true';
    }

    if (hasPendingUploads(state)) {
        setError(state, state.root.dataset.errorPending || 'Wait until the upload is complete.');
        return false;
    }

    if (state.required && !hasUploadedReferences(state)) {
        setError(state, state.root.dataset.errorRequired || 'Select at least one file.');
        return false;
    }

    clearError(state);
    return true;
}

function validateWhenUploadsSettled(state) {
    if (hasPendingUploads(state)) {
        return;
    }

    validate(state);
}

function setError(state, message) {
    state.input.classList.add('input-validation-error');
    state.input.setAttribute('aria-invalid', 'true');

    if (state.field) {
        state.field.classList.add('is-invalid');
        state.field.dataset.touched = 'true';
    }

    setValidationMessage(state.validationMessage, message);
    dispatchUploadEvent(state, 'sds-upload:error-visible', { message });
}

function clearError(state) {
    state.input.classList.remove('input-validation-error');
    state.input.setAttribute('aria-invalid', 'false');

    clearValidationMessage(state.validationMessage);

    if (state.field) {
        state.field.classList.remove('is-invalid');
    }

    dispatchUploadEvent(state, 'sds-upload:error-cleared');
}

function syncUploadVisibility(state) {
    state.drop.hidden = state.files.size >= state.maxFiles;
}